import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX } = FiIcons;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Nature Green', path: '/310' },
    { name: 'Royal Blue', path: '/414' },
    { name: 'Cozy Grey', path: '/612' },
    { name: 'Amenities', path: '/amenities' },
    { name: 'Nearby', path: '/nearby' },
  ];

  // We'll use black text and white background consistently for maximum readability
  const textColorClass = 'text-black';
  const logoColorClass = 'text-black';

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/98 backdrop-blur-md py-3 shadow-md border-b border-black/5' 
          : 'bg-white py-4 shadow-sm border-b border-black/5'
      }`}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center relative h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center group z-10 shrink-0" aria-label="7601 Canada Home">
          <div className="flex flex-col leading-none">
            <span className={`text-2xl md:text-3xl font-serif font-black tracking-tighter uppercase transition-colors duration-300 ${logoColorClass}`}>
              7601 CANADA
            </span>
            <span className="text-[9px] font-bold tracking-[0.3em] uppercase text-accent-orange">
              Orlando
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav 
          className="hidden md:flex flex-1 justify-center items-center gap-8 px-8" 
          role="navigation" 
          aria-label="Main Navigation"
        >
          <div className="flex items-center gap-6 lg:gap-8 lg:mr-24">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-[10px] lg:text-[11px] font-black uppercase tracking-[0.2em] lg:tracking-[0.25em] transition-all duration-300 hover:text-accent-orange outline-none focus:ring-2 focus:ring-black rounded-sm border-b-2 py-1 ${
                  location.pathname === link.path 
                    ? 'text-accent-orange border-accent-orange' 
                    : `${textColorClass} border-transparent`
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </nav>

        {/* Mobile Toggle */}
        <div className="md:hidden ml-auto">
          <button 
            className="p-3 rounded-2xl focus:outline-none focus:ring-2 transition-all duration-300 text-black bg-black/5 hover:bg-black/10 focus:ring-black z-10"
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-label="Toggle Menu"
          >
            <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden absolute top-full left-0 w-full bg-white border-b border-black/10 shadow-2xl overflow-hidden z-40"
          >
            <div className="px-8 py-10 flex flex-col gap-6 items-center text-center">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`w-full py-3 text-2xl font-serif font-black uppercase tracking-tight transition-colors ${
                    location.pathname === link.path ? 'text-accent-orange' : 'text-black'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;